import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import axios from "axios";

const AuthContext = createContext(null);

function getSavedUser() {
  const savedUser = localStorage.getItem("user");

  if (!savedUser) {
    return null;
  }

  try {
    return JSON.parse(savedUser);
  } catch (err) {
    console.error("Could not parse saved user:", err);
    localStorage.removeItem("user");
    return null;
  }
}

function getSavedToken() {
  return localStorage.getItem("token");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getSavedUser());
  const [token, setToken] = useState(() => getSavedToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  const login = useCallback((loginResponse) => {
    console.log("AuthContext login received:", loginResponse);

    let userData = null;
    let tokenData = null;

    if (loginResponse?.user) {
      userData = loginResponse.user;
    } else {
      userData = loginResponse;
    }

    if (loginResponse?.token) {
      tokenData = loginResponse.token;
    } else if (loginResponse?.jwt) {
      tokenData = loginResponse.jwt;
    } else if (loginResponse?.accessToken) {
      tokenData = loginResponse.accessToken;
    }

    setUser(userData);
    setToken(tokenData);

    if (userData) {
      localStorage.setItem("user", JSON.stringify(userData));
    } else {
      localStorage.removeItem("user");
    }

    if (tokenData) {
      localStorage.setItem("token", tokenData);
    } else {
      localStorage.removeItem("token");
    }

    console.log("Saved user:", userData);
    console.log("Saved token:", tokenData);
  }, []);

  const updateUser = useCallback((updatedFields) => {
    setUser((previousUser) => {
      if (!previousUser) {
        return previousUser;
      }

      const updatedUser = {
        ...previousUser,
        ...updatedFields
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));

      return updatedUser;
    });
  }, []);

  const replaceUser = useCallback((updatedUser) => {
    setUser(updatedUser);

    if (updatedUser) {
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } else {
      localStorage.removeItem("user");
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try { const response = await axios.get("/api/users/me", { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }); replaceUser(response.data); return response.data; } catch (err) { console.error("Could not refresh user:", err); return null; }
  }, []);

  const clearLocalSession = useCallback(() => {
    setUser(null);
    setToken(null);

    localStorage.removeItem("user");
    localStorage.removeItem("token");
  }, []);

  const logout = useCallback(async () => {
    const currentToken = localStorage.getItem("token");

    if (currentToken) {
      try {
        await axios.post(
          "/api/auth/logout",
          {},
          {
            headers: {
              Authorization: `Bearer ${currentToken}`
            }
          }
        );
      } catch (err) {
        console.error("Backend logout error:", err);
      }
    }

    clearLocalSession();
  }, [clearLocalSession]);

  const isAuthenticated = Boolean(user) && Boolean(token);

  const value = useMemo(() => {
    return {
      user,
      token,
      loading,
      login,
      logout,
      updateUser,
      replaceUser,
      refreshUser,
      isAuthenticated
    };
  }, [
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    replaceUser,
    refreshUser,
    isAuthenticated
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
